import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-hook-form';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useResizeListener } from '@Hooks/custom/useResizeListener.ts';
import { SUBMIT_BUTTON_ID } from '@Types/consts.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import type { Control, FieldValues } from 'react-hook-form';
import type React from 'react';
interface IFormFooterProps {
  isLoading: boolean;
  onSubmit: () => void;
  maxWidth?: number;
  formMethods: { control: Control<FieldValues> };
  customBackHandling?: () => void;
  customSubmitTitle?: string;
  footerInputSlot?: React.ReactNode;
  shouldStrechCta?: boolean;
  isFormValid?: boolean;
  skipFormValidation?: boolean;
  centerCta?: boolean;
  showBackButton?: boolean;
  showNextButton?: boolean;
}

export const FormFooter = memo(
  ({
    isLoading,
    onSubmit,
    maxWidth,
    formMethods,
    customBackHandling,
    customSubmitTitle,
    footerInputSlot,
    shouldStrechCta,
    isFormValid,
    skipFormValidation,
    centerCta,
    showBackButton = true,
    showNextButton = true,
  }: IFormFooterProps) => {
    const { t } = useTranslation();
    const { isValid, errors } = useFormState({ control: formMethods.control });

    const { navigateToPreviousPage, isAllowedPrevPage } = useNavigationManager();
    const { isMobile } = useResizeListener();

    const getDynamicMobileLayout = () => {
      if (footerInputSlot) {
        return 'w-full flex flex-col new-tab-landscape:flex-row items-center justify-between gap-[20px] mx-auto';
      }
      if (centerCta) {
        return 'w-full flex items-center justify-center mx-auto';
      }
      return 'w-full flex items-center justify-between mx-auto';
    };
    const hasErrors = Object.keys(errors).length > 0;
    const isDisabled =
      (!skipFormValidation && (!isValid || hasErrors)) ||
      isLoading ||
      (isFormValid !== undefined && !isFormValid);

    // Hide footer on mobile if next button is hidden
    if (isMobile && !showNextButton) {
      return null;
    }

    return (
      <div
        className='w-full p-[16px] md:p-[24px] z-40 bg-surface-primary-bw-00 sm:block shrink-0 border-t border-outline-primary-bw-03'
        id='page-footer'>
        <div
          style={{ maxWidth: maxWidth ? `${maxWidth}px` : '500px' }}
          className={getDynamicMobileLayout()}>
          {footerInputSlot ? (
            <div className='min-w-0 w-full'>{footerInputSlot}</div>
          ) : !centerCta ? (
            <div
              className={
                !showBackButton || !isAllowedPrevPage
                  ? 'hidden sm:block sm:opacity-0 sm:pointer-events-none w-[115px]'
                  : 'hidden sm:block sm:opacity-100 sm:pointer-events-auto w-[115px]'
              }>
              <LiliButton
                onClick={() => (customBackHandling ?? navigateToPreviousPage)()}
                title='Back'
                id='back'
                data-testid='back-button'
                type={LiliButtonType.SECONDARY}
                className='text-text-main-primary'
              />
            </div>
          ) : null}

          <div
            className={
              !showNextButton
                ? 'hidden-placeholder'
                : twMerge('w-full min-w-[115px] shrink-0', !shouldStrechCta && 'sm:w-fit')
            }>
            <LiliButton
              onClick={() => onSubmit()}
              isLoading={isLoading}
              disabled={isDisabled}
              id={SUBMIT_BUTTON_ID}
              type={LiliButtonType.PRIMARY}
              title={customSubmitTitle || t('global.FORM_SUBMIT')}
              className='new-tab-landscape:whitespace-nowrap max-h-none'
              data-testid='continue-button'
            />
          </div>
        </div>
      </div>
    );
  }
);

FormFooter.displayName = 'FormFooter';
